import { logAmplitudeEvent } from 'amplitude/amplitude'
import { authSelectors } from 'ducks/auth'
import { styled, TextField, Typography, useTheme } from 'opensolar-ui'
import { loadMapsLibrary } from 'pages/googleLeads/elements/quoteAndMap/loadMapsLibrary'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import {
  calculateDistanceKm,
  extractWarehouseLocations,
  formatDistanceMiles,
  getDistributors,
  getNationalStock,
  hasOnlyGlobalWarehouse,
} from './helpers'
import { ProcessedWarehouse, WarehouseLocatorContentProps } from './types'

const MARKER_SVG_PATH =
  'M12 29.0928C11.6902 29.0928 11.3805 29.0398 11.0707 28.9339C10.7607 28.8276 10.4804 28.6633 10.2297 28.4408C8.80313 27.1361 7.46807 25.7924 6.22456 24.4099C4.98132 23.0276 3.90064 21.6456 2.98251 20.2641C2.06412 18.8826 1.33744 17.5133 0.802464 16.1562C0.267488 14.7989 0 13.4881 0 12.2239C0 8.58311 1.18667 5.63561 3.56002 3.38136C5.93362 1.12712 8.74695 0 12 0C15.253 0 18.0664 1.12712 20.44 3.38136C22.8133 5.63561 24 8.58311 24 12.2239C24 13.4881 23.7325 14.7963 23.1975 16.1483C22.6626 17.5007 21.9385 18.8701 21.0254 20.2566C20.1121 21.6431 19.0338 23.0251 17.7905 24.4024C16.5473 25.78 15.2122 27.121 13.7854 28.4255C13.5384 28.6479 13.2577 28.8148 12.9432 28.926C12.6289 29.0372 12.3145 29.0928 12 29.0928ZM12.0028 14.7754C12.7937 14.7754 13.4699 14.4959 14.0314 13.9368C14.5931 13.3778 14.874 12.7058 14.874 11.921C14.874 11.1361 14.5923 10.4649 14.029 9.90744C13.4657 9.35026 12.7884 9.07166 11.9972 9.07166C11.2063 9.07166 10.5301 9.35118 9.9686 9.9102C9.40686 10.4692 9.12599 11.1413 9.12599 11.9265C9.12599 12.7114 9.40766 13.3824 9.97099 13.9396C10.5343 14.4968 11.2116 14.7754 12.0028 14.7754Z'

const Container = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
}))

const SearchSection = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
}))

const InputLabel = styled('label')(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(0.75),
  fontSize: theme.typography.pxToRem(14),
  fontWeight: theme.typography.fontWeightMedium,
}))

const LinkContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
}))

const QuickLink = styled('span')<{ disabled?: boolean }>(({ theme, disabled }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.typography.fontWeightMedium,
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: disabled ? theme.palette.grey[400] : theme.palette.blue[900],
  '&:hover': {
    textDecoration: disabled ? 'none' : 'underline',
  },
}))

const SupplierTabsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  marginBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
}))

const SupplierTab = styled('div')<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: theme.spacing(1, 1.5),
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontSize: theme.typography.pxToRem(14),
  fontWeight: theme.typography.fontWeightMedium,
  borderBottom: `1px solid ${selected ? theme.palette.blue[700] : theme.palette.grey[100]}`,
  color: selected ? theme.palette.blue[700] : theme.palette.grey[700],
  transition: 'all 0.2s',
  '&:hover': {
    color: theme.palette.blue[700],
  },
}))

const ContentGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '60% 40%',
  gap: theme.spacing(2),
  height: 450,
  overflow: 'hidden',
}))

const WarehouseList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  height: '100%',
  overflowY: 'auto',
  paddingRight: theme.spacing(1),
}))

const WarehouseCard = styled('div')<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: theme.spacing(1, 0),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  cursor: 'pointer',
  backgroundColor: selected ? theme.palette.blue[300] : 'transparent',
}))

const StockText = styled('span')<{ inStock?: boolean }>(({ theme, inStock }) => ({
  fontSize: theme.typography.pxToRem(16),
  lineHeight: theme.typography.pxToRem(24),
  fontWeight: theme.typography.fontWeightMedium,
  color: inStock ? theme.palette.green[800] : theme.palette.red[800],
}))

const WarehouseName = styled('div')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  lineHeight: theme.typography.pxToRem(24),
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[900],
}))

const WarehouseAddress = styled('div')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  lineHeight: theme.typography.pxToRem(20),
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.grey[600],
  marginTop: theme.spacing(0.5),
}))

const DistanceText = styled('div')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.blue[900],
}))

const MAP_HEIGHT = 450

const MapContainer = styled('div')(({ theme }) => ({
  overflow: 'hidden',
  height: '100%',
  minHeight: MAP_HEIGHT,
  backgroundColor: theme.palette.grey[200],
  position: 'relative',
}))

const MapElement = styled('div')({
  width: '100%',
  height: '100%',
  minHeight: MAP_HEIGHT,
})

const LoadingOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255,255,255,0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
}))

const EmptyState = styled('div')(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(5),
  color: theme.palette.grey[500],
}))

const NationalStockText = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  lineHeight: theme.typography.pxToRem(24),
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.green[800],
}))

const MarkerPopup = styled('div')(({ theme }) => ({
  position: 'absolute',
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[2],
  zIndex: 1000,
  height: 100,
  width: 300,
  padding: theme.spacing(1.5),
  boxSizing: 'border-box',
  transform: 'translate(-50%, -100%)',
}))

const PopupCloseButton = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(0.5),
  right: theme.spacing(0.5),
  background: 'none',
  border: 'none',
  fontSize: theme.typography.pxToRem(16),
  cursor: 'pointer',
  color: theme.palette.grey[600],
  padding: theme.spacing(0.25, 0.75),
  lineHeight: 1,
}))

const PopupHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
}))

const PopupName = styled('div')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[900],
}))

const PopupStock = styled('span')<{ inStock?: boolean }>(({ theme, inStock }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: theme.typography.fontWeightMedium,
  whiteSpace: 'nowrap',
  color: inStock ? theme.palette.green[900] : theme.palette.red[900],
}))

const PopupDetail = styled('div')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.grey[600],
  marginTop: theme.spacing(0.5),
}))

export const WarehouseLocatorMapContent: FC<WarehouseLocatorContentProps> = ({
  data,
  projectAddress,
  orgAddress,
  orgCountry = 'United Kingdom',
  selectedDistributor: initialDistributor,
  open = true,
  amplitudeEventType,
}) => {
  const translate = useTranslate()
  const theme = useTheme()
  const apiKeyGoogle = useSelector(authSelectors.getApiKeyGoogle)

  const mapRef = useRef<HTMLDivElement>(null)
  const [mapLibraryLoaded, setMapLibraryLoaded] = useState(false)
  const [map, setMap] = useState<any>(null)
  const [placesService, setPlacesService] = useState<any>(null)
  const markersRef = useRef<any[]>([])
  const startLocationRef = useRef<{ lat: number; lng: number } | null>(null)
  const mapBoundsRef = useRef<any>(null)
  const selectedWarehouseRef = useRef<ProcessedWarehouse | null>(null)
  const deselectMarkerRef = useRef<() => void>(() => {})

  const [searchValue, setSearchValue] = useState(projectAddress || orgAddress || orgCountry)
  const initialSearch = projectAddress || orgAddress || orgCountry
  const [committedSearch, setCommittedSearch] = useState(initialSearch)
  const isUserSearch = useRef(false)
  const [selectedDistributor, setSelectedDistributor] = useState<string | undefined>(initialDistributor)
  const [selectedWarehouse, setSelectedWarehouse] = useState<ProcessedWarehouse | null>(null)
  const [showMarkerInfo, setShowMarkerInfo] = useState(false)
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null)
  const [distances, setDistances] = useState<Map<string, number>>(new Map())

  const distributors = useMemo(() => getDistributors(data), [data])
  const warehouses = useMemo(() => extractWarehouseLocations(data, selectedDistributor), [data, selectedDistributor])
  const isGlobalWarehouse = useMemo(
    () => hasOnlyGlobalWarehouse(data, selectedDistributor),
    [data, selectedDistributor]
  )
  const nationalStock = useMemo(() => getNationalStock(data, selectedDistributor), [data, selectedDistributor])

  useEffect(() => {
    if (distributors.length > 0 && !selectedDistributor) {
      setSelectedDistributor(distributors[0].key)
    }
  }, [distributors, selectedDistributor])

  useEffect(() => {
    //@ts-ignore
    if (window?.google?.maps) {
      setMapLibraryLoaded(true)
      return
    }
    if (apiKeyGoogle) {
      loadMapsLibrary('Google', apiKeyGoogle)
        .then(() => {
          setMapLibraryLoaded(true)
        })
        .catch(() => {
          console.error('Error loading Google Maps library')
        })
    }
  }, [apiKeyGoogle])

  useEffect(() => {
    if (!open || !mapLibraryLoaded || !mapRef.current || isGlobalWarehouse) return

    //@ts-ignore
    const newMap = new window.google.maps.Map(mapRef.current, {
      center: { lat: 52.5, lng: -1.5 },
      zoom: 6,
      minZoom: 5,
      mapTypeControl: false,
      disableDefaultUI: false,
      streetViewControl: false,
      fullscreenControl: false,
    })

    //@ts-ignore
    const newPlacesService = new window.google.maps.places.PlacesService(newMap)

    setMap(newMap)
    setPlacesService(newPlacesService)

    return () => {
      setMap(null)
      setPlacesService(null)
    }
  }, [open, mapLibraryLoaded, isGlobalWarehouse])

  // Trigger map resize after modal opens so the map fills the visible container
  useEffect(() => {
    if (!map || !open) return
    const t = setTimeout(() => {
      try {
        // @ts-ignore
        window.google?.maps?.event?.trigger(map, 'resize')
      } catch (_) {}
    }, 100)
    return () => clearTimeout(t)
  }, [map, open])

  useEffect(() => {
    if (!map) return

    const dragEndListener = map.addListener('dragend', () => {
      if (!mapBoundsRef.current) return

      const center = map.getCenter()
      if (!mapBoundsRef.current.contains(center)) {
        const bounds = mapBoundsRef.current
        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()

        let newLat = center.lat()
        let newLng = center.lng()

        if (newLat > ne.lat()) newLat = ne.lat()
        if (newLat < sw.lat()) newLat = sw.lat()
        if (newLng > ne.lng()) newLng = ne.lng()
        if (newLng < sw.lng()) newLng = sw.lng()

        map.panTo({ lat: newLat, lng: newLng })
      }
    })

    return () => {
      //@ts-ignore
      if (dragEndListener) window.google?.maps?.event?.removeListener(dragEndListener)
    }
  }, [map])

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      if (marker && marker.setMap) {
        marker.setMap(null)
      }
    })
    markersRef.current = []
    setPopupPosition(null)
    setSelectedWarehouse(null)
    setShowMarkerInfo(false)
  }, [])

  const findPlace = useCallback(
    (query: string): Promise<{ lat: number; lng: number; place_id?: string } | null> => {
      return new Promise((resolve) => {
        if (!placesService) {
          resolve(null)
          return
        }
        const request = {
          query: query,
          fields: ['geometry', 'place_id'],
        }
        placesService.findPlaceFromQuery(request, (results: any, status: string) => {
          //@ts-ignore
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.[0]?.geometry?.location) {
            const loc = results[0].geometry.location
            resolve({ lat: loc.lat(), lng: loc.lng(), place_id: results[0].place_id })
          } else {
            resolve(null)
          }
        })
      })
    },
    [placesService]
  )

  const getPlaceDetails = useCallback(
    (placeId: string): Promise<Record<string, any> | null> => {
      return new Promise((resolve) => {
        if (!placesService) {
          resolve(null)
          return
        }
        placesService.getDetails(
          { placeId, fields: ['address_components'] },
          (place: any, status: string) => {
            //@ts-ignore
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.address_components) {
              const parts: Record<string, any> = {}
              for (const comp of place.address_components) {
                const types: string[] = comp.types ?? []
                if (types.includes('locality') || types.includes('postal_town') || types.includes('sublocality')) {
                  parts.locality = parts.locality ?? comp.long_name
                }
                if (types.includes('administrative_area_level_1')) {
                  parts.state = comp.short_name
                }
                if (types.includes('country')) {
                  parts.country_iso2 = comp.short_name
                }
              }
              resolve(parts)
            } else {
              resolve(null)
            }
          }
        )
      })
    },
    [placesService]
  )

  useEffect(() => {
    if (!map || !placesService) return

    let isCancelled = false
    clearMarkers()

    const currentWarehouses = extractWarehouseLocations(data, selectedDistributor)
    if (currentWarehouses.length === 0) return

    const updateMarkers = async () => {
      const newDistances = new Map<string, number>()
      //@ts-ignore
      const bounds = new window.google.maps.LatLngBounds()
      let closestMarker: any = null
      let closestDistance = Infinity
      let searchLocationDetails: Record<string, any> | null = null

      if (committedSearch && !isCancelled) {
        const startLocation = await findPlace(committedSearch)
        if (isCancelled) return

        if (startLocation) {
          startLocationRef.current = startLocation
          if (startLocation.place_id) {
            searchLocationDetails = await getPlaceDetails(startLocation.place_id)
          }
        }
      } else {
        startLocationRef.current = null
      }

      for (const warehouse of currentWarehouses) {
        if (isCancelled) return

        const location = await findPlace(warehouse.address)
        if (isCancelled) return

        if (location) {
          //@ts-ignore
          const position = new window.google.maps.LatLng(location.lat, location.lng)
          bounds.extend(position)

          let dist: number | undefined
          if (startLocationRef.current) {
            dist = calculateDistanceKm(
              startLocationRef.current.lat,
              startLocationRef.current.lng,
              location.lat,
              location.lng
            )
            newDistances.set(warehouse.key, dist)
          }

          //@ts-ignore
          const marker = new window.google.maps.Marker({
            position: position,
            map: map,
            title: warehouse.name,
            icon: {
              path: MARKER_SVG_PATH,
              fillColor: '#1751D0',
              fillOpacity: 1,
              strokeColor: 'transparent',
              strokeWeight: 0,
              scale: 1,
              //@ts-ignore
              anchor: new window.google.maps.Point(12, 29),
            },
          })

          ;(marker as any).warehouseData = { ...warehouse, distance: dist }

          marker.addListener('click', () => {
            const wh = (marker as any).warehouseData as ProcessedWarehouse

            if (selectedWarehouseRef.current?.key === wh.key) {
              deselectMarkerRef.current()
              return
            }

            selectedWarehouseRef.current = wh
            setSelectedWarehouse(wh)
            setShowMarkerInfo(true)

            logAmplitudeEvent('hardware_product_warehouse_selected', {
              action: 'click',
              source: 'warehouse_locator_modal',
              context: 'warehouse_selected',
              distributor_key: selectedDistributor,
              warehouse_key: wh.key,
              distance_km: wh.distance ?? null,
              total_stock: wh.totalStock,
              surface: 'map',
            }, amplitudeEventType)

            const markerPosition = marker.getPosition()
            if (markerPosition && map) {
              map.panTo(markerPosition)
            }
          })

          markersRef.current.push(marker)

          if (dist !== undefined && dist < closestDistance) {
            closestDistance = dist
            closestMarker = marker
          }
        }
      }

      if (!isCancelled) {
        setDistances(newDistances)
        if (markersRef.current.length > 0) {
          if (startLocationRef.current && closestMarker) {
            const closestPosition = closestMarker.getPosition()
            map.setCenter(closestPosition)
            map.setZoom(10)
          } else {
            map.fitBounds(bounds)
          }

          const ne = bounds.getNorthEast()
          const sw = bounds.getSouthWest()
          const latPadding = (ne.lat() - sw.lat()) * 0.5
          const lngPadding = (ne.lng() - sw.lng()) * 0.5

          //@ts-ignore
          const restrictedBounds = new window.google.maps.LatLngBounds(
            { lat: sw.lat() - latPadding, lng: sw.lng() - lngPadding },
            { lat: ne.lat() + latPadding, lng: ne.lng() + lngPadding }
          )
          mapBoundsRef.current = restrictedBounds
        }

        const sortedWarehouses = currentWarehouses
          .map((w) => ({ ...w, distance: newDistances.get(w.key) }))
          .sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999))

        if (isUserSearch.current) {
          isUserSearch.current = false
          logAmplitudeEvent('hardware_product_location_search_complete', {
            action: 'response',
            source: 'warehouse_locator_modal',
            context: 'location_search_complete',
            distributor_key: selectedDistributor,
            search_city: searchLocationDetails?.locality ?? null,
            search_state: searchLocationDetails?.state ?? null,
            search_country: searchLocationDetails?.country_iso2 ?? null,
            closest_warehouses: sortedWarehouses.slice(0, 3).map((w) => ({
              warehouse_key: w.key,
              distance_km: w.distance != null ? Math.round(w.distance * 10) / 10 : null,
              total_stock: w.totalStock,
            })),
          }, amplitudeEventType)
        }
      }
    }

    updateMarkers()

    return () => {
      isCancelled = true
    }
  }, [map, placesService, committedSearch, selectedDistributor, data, clearMarkers, findPlace, getPlaceDetails])

  useEffect(() => {
    return () => {
      clearMarkers()
    }
  }, [clearMarkers])

  const deselectMarker = useCallback(() => {
    selectedWarehouseRef.current = null
    setSelectedWarehouse(null)
    setShowMarkerInfo(false)
    setPopupPosition(null)
  }, [])

  useEffect(() => {
    deselectMarkerRef.current = deselectMarker
  }, [deselectMarker])

  const updatePopupPosition = useCallback(() => {
    const currentWarehouse = selectedWarehouseRef.current
    if (!showMarkerInfo || !currentWarehouse || !map || !mapRef.current) {
      setPopupPosition(null)
      return
    }

    const marker = markersRef.current.find((m) => (m as any).warehouseData?.key === currentWarehouse.key)
    if (!marker) {
      setPopupPosition(null)
      return
    }

    const markerPosition = marker.getPosition()
    if (!markerPosition) {
      setPopupPosition(null)
      return
    }

    const bounds = map.getBounds()
    if (!bounds || !bounds.contains(markerPosition)) {
      deselectMarker()
      return
    }

    const scale = Math.pow(2, map.getZoom())
    const projection = map.getProjection()
    if (!projection) {
      setPopupPosition(null)
      return
    }

    const worldPoint = projection.fromLatLngToPoint(markerPosition)
    const centerPoint = projection.fromLatLngToPoint(map.getCenter())
    if (!worldPoint || !centerPoint) {
      setPopupPosition(null)
      return
    }

    const mapContainer = mapRef.current.getBoundingClientRect()
    const x = (worldPoint.x - centerPoint.x) * scale + mapContainer.width / 2
    const y = (worldPoint.y - centerPoint.y) * scale + mapContainer.height / 2 - 29

    if (x < 0 || x > mapContainer.width || y < -100 || y > mapContainer.height) {
      deselectMarker()
      return
    }

    setPopupPosition({ x, y })
  }, [showMarkerInfo, map, deselectMarker])

  useEffect(() => {
    if (!map) return

    const listeners: any[] = []

    listeners.push(map.addListener('idle', updatePopupPosition))
    listeners.push(map.addListener('center_changed', updatePopupPosition))
    listeners.push(map.addListener('zoom_changed', updatePopupPosition))

    updatePopupPosition()

    return () => {
      listeners.forEach((listener) => {
        //@ts-ignore
        if (listener) window.google?.maps?.event?.removeListener(listener)
      })
    }
  }, [map, updatePopupPosition])

  const warehousesWithDistance = useMemo(
    () =>
      warehouses
        .map((w) => ({ ...w, distance: distances.get(w.key) }))
        .sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999)),
    [warehouses, distances]
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
  }

  const handleUseProjectAddress = () => {
    if (projectAddress) {
      setSearchValue(projectAddress)
      isUserSearch.current = true
      setCommittedSearch(projectAddress)
    }
  }

  const handleUseOrgAddress = () => {
    const addr = orgAddress || orgCountry
    if (addr) {
      setSearchValue(addr)
      isUserSearch.current = true
      setCommittedSearch(addr)
    }
  }

  const handleDistributorChange = (key: string) => {
    setSelectedDistributor(key)
    deselectMarker()
  }

  const handleWarehouseClick = (warehouse: ProcessedWarehouse) => {
    if (selectedWarehouse?.key === warehouse.key) {
      deselectMarker()
      return
    }

    selectedWarehouseRef.current = warehouse
    setSelectedWarehouse(warehouse)
    setShowMarkerInfo(true)

    logAmplitudeEvent('hardware_product_warehouse_selected', {
      action: 'click',
      source: 'warehouse_locator_modal',
      context: 'warehouse_selected',
      distributor_key: selectedDistributor,
      warehouse_key: warehouse.key,
      distance_km: warehouse.distance ?? null,
      total_stock: warehouse.totalStock,
      surface: 'list',
    }, amplitudeEventType)

    const marker = markersRef.current.find((m) => (m as any).warehouseData?.key === warehouse.key)
    if (marker && map) {
      const markerPosition = marker.getPosition()
      if (markerPosition) {
        map.panTo(markerPosition)
      }
    }
  }

  return (
    <Container onKeyDown={handleKeyDown}>
      <SearchSection>
        <div>
          <InputLabel>{translate('Enter zip code or city, state')}</InputLabel>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            value={searchValue}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') {
                isUserSearch.current = true
                setCommittedSearch(searchValue)
              }
            }}
            onFocus={() => {
              try {
                window.editor.signals.typingInField.dispatch(true)
              } catch (err) {}
            }}
            onBlur={() => {
              isUserSearch.current = true
              setCommittedSearch(searchValue)
              try {
                window.editor.signals.typingInField.dispatch(false)
              } catch (err) {}
            }}
            placeholder={translate('Search postcode or suburb')}
          />
        </div>
        {projectAddress && (orgAddress || orgCountry) && (
          <LinkContainer>
            <QuickLink onClick={handleUseProjectAddress}>{translate('Use project address')}</QuickLink>
            <QuickLink onClick={handleUseOrgAddress}>{translate('Use org address')}</QuickLink>
          </LinkContainer>
        )}
      </SearchSection>

      <SupplierTabsContainer>
        {distributors.map((dist) => (
          <SupplierTab
            key={dist.key}
            selected={selectedDistributor === dist.key}
            onClick={() => handleDistributorChange(dist.key)}
          >
            {dist.name}
          </SupplierTab>
        ))}
      </SupplierTabsContainer>

      {isGlobalWarehouse ? (
        <NationalStockText>{translate('%{count} in stock nationally', { count: nationalStock })}</NationalStockText>
      ) : (
        <ContentGrid>
          <WarehouseList>
            {warehousesWithDistance.length === 0 ? (
              <EmptyState>{translate('No warehouse locations found for this distributor')}</EmptyState>
            ) : (
              warehousesWithDistance.map((warehouse) => {
                const inStock = warehouse.totalStock > 0
                return (
                  <WarehouseCard
                    key={warehouse.key}
                    selected={selectedWarehouse?.key === warehouse.key}
                    onClick={() => handleWarehouseClick(warehouse)}
                  >
                    <StockText inStock={inStock}>
                      {inStock
                        ? translate('%{count} in stock', { count: warehouse.totalStock })
                        : translate('Out of stock')}
                    </StockText>
                    <WarehouseName>{warehouse.name}</WarehouseName>
                    <WarehouseAddress>{warehouse.address}</WarehouseAddress>
                    {warehouse.distance !== undefined && (
                      <DistanceText>
                        {translate('Distance')}: {formatDistanceMiles(warehouse.distance)}
                      </DistanceText>
                    )}
                  </WarehouseCard>
                )
              })
            )}
          </WarehouseList>

          <MapContainer>
            <MapElement ref={mapRef} />

            {!mapLibraryLoaded && (
              <LoadingOverlay>
                <Typography>{translate('Loading map...')}</Typography>
              </LoadingOverlay>
            )}

            {showMarkerInfo && selectedWarehouse && popupPosition && (
              <MarkerPopup style={{ left: popupPosition.x, top: popupPosition.y }}>
                <PopupCloseButton onClick={deselectMarker}>×</PopupCloseButton>
                <PopupHeader>
                  <PopupName>{selectedWarehouse.name}</PopupName>
                  <PopupStock inStock={selectedWarehouse.totalStock > 0}>
                    {selectedWarehouse.totalStock > 0
                      ? translate('%{count} in stock', { count: selectedWarehouse.totalStock })
                      : translate('Out of stock')}
                  </PopupStock>
                </PopupHeader>
                <PopupDetail>{selectedWarehouse.address}</PopupDetail>
                {selectedWarehouse.distance !== undefined && (
                  <PopupDetail>
                    {translate('Distance')}: {formatDistanceMiles(selectedWarehouse.distance)}
                  </PopupDetail>
                )}
              </MarkerPopup>
            )}
          </MapContainer>
        </ContentGrid>
      )}
    </Container>
  )
}

export default WarehouseLocatorMapContent
